import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  ArchiveAgendaReq,
  ArchiveAgendaRes,
  AssignAgendaRolesReq,
  AssignAgendaRolesRes,
  CreateAgendaReq,
  CreateAgendaRes,
  CreateOrUpdateAgendaVisibilitiesReq,
  CreateOrUpdateAgendaVisibilitiesRes,
  GetAgendaByIdReq,
  GetAgendaByIdRes,
  GetAgendaRolesReq,
  GetAgendaRolesRes,
  GetAgendaVisibilitiesReq,
  GetAgendaVisibilitiesRes,
  GetAgendasCountReq,
  GetAgendasCountRes,
  GetAgendasReq,
  GetAgendasRes,
  RemoveAgendaRolesReq,
  UpdateAgendaReq,
  UpdateAgendaRes,
} from 'types';
import type { flowModulesAndAgendasTableDataScheme } from 'utils/table/admin/flow/modulesAndAgendas';
import type { flowDetailRoleSectionDataScheme } from 'utils/table/admin/flow/roles';

export const agendaService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getAgendas: build.query<GetAgendasRes, GetAgendasReq<typeof flowModulesAndAgendasTableDataScheme>>({
      query: (params) => ({
        url: 'Agendas',
        method: 'GET',
        params,
      }),
      providesTags: [{ type: TAG_TYPES.AGENDAS, id: 'LIST' }],
    }),
    CreateNewAgenda: build.mutation<CreateAgendaRes, CreateAgendaReq>({
      query: (body) => ({
        url: 'Agendas',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: TAG_TYPES.AGENDAS, id: 'LIST' },
        { type: TAG_TYPES.AGENDAS, id: 'COUNT' },
        { type: TAG_TYPES.AGENDAS, id: 'VISIBILITIES' },
      ],
    }),
    GetAgendasCount: build.query<GetAgendasCountRes, GetAgendasCountReq>({
      query: (params) => ({
        url: 'Agendas/Count',
        method: 'GET',
        params,
      }),

      providesTags: [{ type: TAG_TYPES.AGENDAS, id: 'COUNT' }],
    }),
    GetAgendaById: build.query<GetAgendaByIdRes, GetAgendaByIdReq & { suppressErrorToast?: boolean }>({
      query: ({ id }) => ({
        url: `Agendas/${id}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.id,

      providesTags: [{ type: TAG_TYPES.AGENDAS, id: 'DETAIL' }],
    }),
    UpdateAgenda: build.mutation<UpdateAgendaRes, UpdateAgendaReq>({
      query: ({ id, body }) => ({
        url: `Agendas/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        { type: TAG_TYPES.AGENDAS, id: 'LIST' },
        {
          type: TAG_TYPES.AGENDAS,
          id: 'DETAIL',
        },
        {
          type: TAG_TYPES.AGENDAS,
          id: 'VISIBILITIES',
        },
      ],
    }),
    ArchiveAgenda: build.mutation<ArchiveAgendaRes, ArchiveAgendaReq>({
      query: ({ id }) => ({
        url: `Agendas/${id}`,
        method: 'DELETE',
      }),

      invalidatesTags: [
        { type: TAG_TYPES.AGENDAS, id: 'LIST' },
        {
          type: TAG_TYPES.AGENDAS,
          id: 'DETAIL',
        },
        {
          type: TAG_TYPES.AGENDAS,
          id: 'VISIBILITIES',
        },
      ],
    }),
    GetAgendaVisibilities: build.query<GetAgendaVisibilitiesRes, GetAgendaVisibilitiesReq>({
      query: ({ id }) => ({
        url: `Agendas/${id}/Visibilities`,
        method: 'GET',
      }),

      providesTags: [TAG_TYPES.AGENDAS],
    }),
    CreateOrUpdateAgendaVisibilities: build.mutation<CreateOrUpdateAgendaVisibilitiesRes, CreateOrUpdateAgendaVisibilitiesReq>({
      query: ({ id, visibilities }) => ({
        url: `Agendas/${id}/Visibilities`,
        method: 'PUT',
        body: {
          visibilities,
        },

        invalidatesTags: [TAG_TYPES.AGENDAS],
      }),
    }),
    GetAgendaRoles: build.query<GetAgendaRolesRes, GetAgendaRolesReq<typeof flowDetailRoleSectionDataScheme>>({
      query: (data) => ({
        url: `Agendas/${data.agendaId}/Roles/WithUsers`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          roleIds: null,
        },
        params: {
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search,
        },
      }),

      providesTags: [{ type: TAG_TYPES.AGENDAS, id: 'ROLES' }],
    }),
    AssignAgendaRoles: build.mutation<AssignAgendaRolesRes, AssignAgendaRolesReq>({
      query: ({ agendaId, data }) => ({
        url: `Agendas/${agendaId}/Roles`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.AGENDAS, id: 'ROLES' }],
    }),
    RemoveAgendaRole: build.mutation<unknown, RemoveAgendaRolesReq>({
      query: ({ agendaId, roleId }) => ({
        url: `Agendas/${agendaId}/Roles/${roleId}`,
        method: 'DELETE',
      }),

      invalidatesTags: [{ type: TAG_TYPES.AGENDAS, id: 'ROLES' }],
    }),
  }),
});
export const {
  useGetAgendasQuery,
  useLazyGetAgendasQuery,
  useCreateNewAgendaMutation,
  useArchiveAgendaMutation,
  useCreateOrUpdateAgendaVisibilitiesMutation,
  useGetAgendaByIdQuery,
  useGetAgendaVisibilitiesQuery,
  useGetAgendasCountQuery,
  useLazyGetAgendaByIdQuery,
  useLazyGetAgendaVisibilitiesQuery,
  useLazyGetAgendasCountQuery,
  useUpdateAgendaMutation,
  useGetAgendaRolesQuery,
  useAssignAgendaRolesMutation,
  useRemoveAgendaRoleMutation,
} = agendaService;
